import type { ColumnDef } from '@tanstack/react-table';
import { TanTable } from '../../components/TanTable';
import { useCallback, useEffect, useState } from 'react';
import api from '../../services/api';

type Unit = { driver: string; driver_name: string | null; status: string };
type Entry = { id: number; driver: string; enabled: boolean | number; effective_at: string; recorded_at: string; recorded_by: string; notes: string };
type Data = { units: Unit[]; history: Entry[] };

const columns: ColumnDef<Unit>[] = [
  { accessorKey: 'driver', header: 'Unit', sortingFn: 'alphanumeric' },
  { accessorKey: 'driver_name', header: 'Driver' },
  { accessorKey: 'status', header: 'Recorded current status' },
];

export default function SingleDispatchUnits() {
  const [data, setData] = useState<Data | null>(null);
  const [search, setSearch] = useState('');
  const [selectionResetKey, setSelectionResetKey] = useState(0);
  const clearSelection = () => { setSelected([]); setSelectionResetKey(value => value + 1); };
  const [selected, setSelected] = useState<string[]>([]);
  const [enabled, setEnabled] = useState('enabled');
  const [effectiveAt, setEffectiveAt] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const load = useCallback(async () => {
    try {
      const response = await api.get<Data>('/geofences/single-dispatch/units');
      setData(response.data); setError('');
    } catch { setError('Could not load rollout membership. Check page permissions and database setup, then retry.'); }
  }, []);
  useEffect(() => { void load(); }, [load]);
  const save = async (event: React.FormEvent) => {
    event.preventDefault(); setBusy(true); setError(''); setNotice('');
    try {
      await api.post('/geofences/single-dispatch/events', {
        units: selected, enabled: enabled === 'enabled', effective_at: effectiveAt, notes,
      });
      setNotice(`Recorded ${enabled} for ${selected.length} units. GwiMove configuration was not changed.`);
      clearSelection(); setNotes(''); await load();
    } catch { setError('Could not record the change. Check write access, unit selection, effective time, and note.'); }
    finally { setBusy(false); }
  };
  return <section className="p-6 space-y-5 text-body">
    <h1 className="text-xl font-semibold">Single-dispatch units</h1>
    <p>Maintain rollout membership for the analytics. Record the effective time when a unit was enabled or disabled in GwiMove.</p>
    <p className="text-sm text-muted">This page records rollout status in FastCMS. It does not change GwiMove’s LaunchDarkly configuration or confirm that a driver’s session refreshed. Access uses FastCMS permissions.</p>
    <a className="text-blue-600 underline" href="/geofencing/geofence-location-accuracy">Open location accuracy analytics</a>
    {error && <p role="alert" className="text-red-600">{error} <button onClick={() => void load()}>Retry</button></p>}
    {notice && <p role="status">{notice}</p>}
    {!data && !error && <p role="status">Loading units…</p>}
    {data && <>
      <form onSubmit={save} className="space-y-3 border rounded p-4">
        <p className="font-medium">Record a rollout change · {selected.length} units selected</p>
        {selected.length > 0 && <p className="text-sm">Selected: {selected.join(', ')} <button type="button" onClick={clearSelection}>Clear selection</button></p>}
        <div className="flex flex-wrap items-end gap-3">
          <label>Status<select aria-label="Recorded status" value={enabled} onChange={e => setEnabled(e.target.value)} className="block border rounded p-2 bg-panel"><option value="enabled">Enabled</option><option value="disabled">Disabled</option></select></label>
          <label>Effective time (Central)<input aria-label="Effective time (Central)" type="datetime-local" required value={effectiveAt} onChange={e => setEffectiveAt(e.target.value)} className="block border rounded p-2 bg-panel" /></label>
          <label className="grow">Reason / evidence<input aria-label="Reason / evidence" required minLength={3} maxLength={500} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Example: rollout configuration updated; session activation unknown" className="block w-full border rounded p-2 bg-panel" /></label>
          <button disabled={busy || selected.length === 0 || selected.length > 100} className="border rounded px-4 py-2">{busy ? 'Saving…' : 'Record status'}</button>
        </div>
        <p className="text-sm text-muted">Changes are appended to the audit history. For a correction, record the corrected status at the same effective time with an explanation. The latest recorded correction takes precedence.</p>
      </form>
      {selected.length > 100 && <p role="alert">Select at most 100 units before recording a change.</p>}
      <fieldset disabled={busy} className="min-w-0">
        <TanTable<Unit>
          data={data.units}
          columns={columns}
          cardTitle="Units"
          settingsKey="single-dispatch-units"
          getRowId={unit => unit.driver}
          enableRowSelection
          onSelectionChange={units => setSelected(units.map(unit => unit.driver))}
          selectionResetKey={selectionResetKey}
          enableSearch
          searchPlaceholder="Find unit, driver or status..."
          onSearchChange={setSearch}
          enableColumnFilters
          enableRowsPerPage
          defaultPageSize={25}
          defaultSorting={[{ id: 'driver', desc: false }]}
        />
      </fieldset>
      <details><summary className="cursor-pointer font-semibold">Rollout audit history</summary><div className="overflow-auto max-h-96"><table className="min-w-full text-sm"><thead><tr className="text-left">{['Unit', 'Status', 'Effective (Central)', 'Recorded', 'Recorded by', 'Note'].map(h => <th key={h} className="p-2">{h}</th>)}</tr></thead><tbody>
        {data.history.filter(row => row.driver.includes(search)).map(row => <tr key={row.id} className="border-t"><td className="p-2">{row.driver}</td><td>{row.enabled ? 'Enabled' : 'Disabled'}</td><td>{row.effective_at}</td><td>{row.recorded_at}</td><td>{row.recorded_by}</td><td>{row.notes}</td></tr>)}
      </tbody></table></div></details>
    </>}
  </section>;
}
